import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject, Subscription } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";

import { DialogServiceService } from "src/app/_app-core/dialogs/dialog-service.service";
import {
  IfscBankFieldMap,
  IfscLookupService,
  IfscNotFoundError,
} from "src/app/_app-core/services/ifsc-lookup.service";
import {
  ChangeRequestAttachmentRef,
  JswVmdService,
  SubmitChangeRequestResponse,
} from "src/app/_app-core/services/jsw-vmd/jsw-vmd.service";
import { attachTypeForDocumentGroup } from "../change-request-attachment-mapping";
import { ChangeRequestFieldType } from "../change-request-field-metadata";
import {
  BANK_DOCUMENT_FIELD_KEYS,
  BANK_DOCUMENT_LABEL,
  bankDocumentGroupId,
  DOCUMENT_FIELD_GROUPS,
  DocumentFieldGroup,
  documentGroupIdForFieldKey,
  fieldDocumentGroupId,
  isDocumentGroupFieldKey,
  sortFieldsByKeyOrder,
  VENDOR_STANDALONE_FIELD_ORDER,
} from "../change-request-document-groups";
import {
  buildValidators,
  fieldPortalLabel,
  fieldSupportingDocuments,
  fieldValidationHint,
  formatChangeRequestDateValue,
  getFieldMeta,
  getValidationErrorMessage,
  isBankFieldRequired,
  isBankKeyEditable,
  isControlEditableForDiff,
  isFieldEditable,
  isValidIfsc,
  parseChangeRequestDate,
} from "../change-request-field-metadata.util";

interface SectionField {
  key: string;
  label: string;
  type: "text" | "number";
  controlName: string;
  editable: boolean;
  fieldType: ChangeRequestFieldType;
  supportingDocuments: string[];
  validationHint?: string;
}

interface FormSection {
  id: "requestor" | "vendor";
  title: string;
  fields: SectionField[];
}

const BANK_DETAILS_KEY = "VENDOR_BANK_DETAILS";

/** Shown when vendor has no bank rows yet; union with scalar keys from session / resubmit data. */
const DEFAULT_BANK_KEYS: readonly string[] = [
  "ACCOUNT_HOLDER",
  "ACCOUNT_NO",
  "IFSC_CODE",
];

/** Required on every bank row when present in the form. */
const BANK_REQUIRED_FIELD_KEYS: readonly string[] = [
  "ACCOUNT_HOLDER",
  "ACCOUNT_NO",
  "IFSC_CODE",
];

/** Preferred column order for known JSW bank fields; any other keys sort after these. */
/** Form keys auto-filled from the JSW IFSC lookup. */
const IFSC_LOOKUP_FORM_KEYS: readonly string[] = [
  "BANK_NAME",
  "BANK_ADDRESS",
  "BANK_BRANCH",
  "BANK_CITY",
  "BANK_STATE_DESC",
  "BANK_STATE_CODE",
  "BANK_SWIFT_CODE",
  "BANK_COUNTRY",
];

/**
 * IFSC-derived bank fields that must be non-empty on every bank row before
 * submit is allowed (BANK_SWIFT_CODE is optional). These are read-only fields
 * populated by the IFSC fetch, so they are checked by raw value rather than
 * form validity.
 */
const BANK_REQUIRED_DISPLAY_FIELD_KEYS: readonly string[] = [
  "BANK_NAME",
  "BANK_ADDRESS",
  "BANK_BRANCH",
  "BANK_CITY",
  "BANK_STATE_DESC",
  "BANK_STATE_CODE",
  "BANK_COUNTRY",
];

const PREFERRED_BANK_KEY_ORDER: readonly string[] = [
  "ACCOUNT_HOLDER",
  "ACCOUNT_NO",
  "IFSC_CODE",
  "BANK_NAME",
  "BANK_ADDRESS",
  "BANK_BRANCH",
  "BANK_CITY",
  "BANK_STATE_DESC",
  "BANK_STATE_CODE",
  "BANK_SWIFT_CODE",
  "BANK_COUNTRY",
  "BANK_KEY",
  "BANKL",
  "SWIFT_CODE",
  "COUNTRY",
  "CURRENCY",
  "CONTROL_KEY",
  "PARTNER_BANK_TYPE",
  "PARTNER_BANK_ID",
  "BANK_ACCOUNT_NAME",
  "BENEFICIARY_NAME",
];

/** Fixed JSW `STG_COLUMN691` shape; values seeded from `vendorRecord`, then edits applied. */
const STG_COLUMN691_TEMPLATE_KEYS = [
  "PAN_NUMBER",
  "MSME_VALID_START_DATE",
  "MDRM_RECORD_NO",
  "GST_NUMBER",
  "COUNTRY",
  "POSTAL_CODE",
  "STATE_DESCR",
  "VENDOR_BANK_DETAILS",
  "MSME_NO",
  "MOBILE_NO1",
  "EMAIL4",
  "EMAIL5",
  "MSME_STATUS",
  "MOBILE_NO2",
  "MOBILE_NO3",
  "SAP_VENDOR_CODE",
  "MSME_VALID_END_DATE",
  "EMAIL2",
  "EMAIL3",
  "VENDOR_NAME1",
  "STREET1",
  "STATE_CODE",
  "STREET2",
  "EMAIL1",
  "VENDOR_NAME3",
  "STREET3",
  "VENDOR_NAME2",
  "STREET4",
  "VENDOR_NAME4",
  "CITY",
  "GST_CLASSIFICATION",
  "VENDOR_ACCOUNT_GROUP",
] as const;

@Component({
  selector: "app-change-request-create",
  templateUrl: "./change-request-create.component.html",
  styleUrls: ["./change-request-create.component.scss"],
})
export class ChangeRequestCreateComponent implements OnInit, OnDestroy {  
  isCondensed = false;
  isSubmitting = false;
  errorMsg = "";
  loadError = "";

  requestorOptions: Record<string, unknown>[] = [];
  requestorSelectItems: { label: string; idx: number }[] = [];
  selectedRequestorItem: { label: string; idx: number } | null = null;
  selectedRequestorIdx = 0;

  form: FormGroup;
  bankRows: FormArray;
  sections: FormSection[] = [];

  /** Scalar field keys for each `VENDOR_BANK_DETAILS` row (union from vendor + resubmit data). */
  bankRowKeys: string[] = [...DEFAULT_BANK_KEYS];

  /** Per bank row: true while the JSW IFSC lookup is in flight. */
  ifscLookupLoading: boolean[] = [];

  /** Per bank row: last IFSC lookup error message (cleared on success). */
  ifscLookupErrors: string[] = [];

  /**
   * Per bank row: the normalized IFSC that was last successfully fetched (or
   * the row's original IFSC for pre-existing rows). Used to detect when the
   * IFSC has been changed without re-fetching the bank details.
   */
  private ifscFetchedValue: string[] = [];

  /** Keys from the prior request's `updatedFields` (resubmit highlight). */
  highlightedResubmitKeys = new Set<string>();

  /** True while fetching `/jswVMD/detail` for resubmit prefill (blocks submit). */
  resubmitPrefillPending = false;

  /** After a failed submit, show validation styling on all invalid fields. */
  showValidationErrors = false;

  /** After a failed submit, highlight upload controls missing required files. */
  showDocumentErrors = false;

  /** UI-only supporting document files keyed by group id (see document-groups). */
  documentUploads: Record<string, File[]> = {};

  readonly documentFieldGroups = DOCUMENT_FIELD_GROUPS;
  readonly bankDocumentLabel = BANK_DOCUMENT_LABEL;

  readonly jswAuthFailed$ = this.jsw.jswAuthFailed$;

  private originalSnapshot: Record<string, string> = {};
  private originalBankRows: Array<Record<string, string>> = [];
  private vendorRecord: Record<string, unknown> = {};
  private requestorRecord: Record<string, unknown> = {};
  private fieldToSection = new Map<string, "requestor" | "vendor">();

  private authFailedSub?: Subscription;
  private lastAuthFailed = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private jsw: JswVmdService,
    private ifscLookup: IfscLookupService,
    private dialogs: DialogServiceService,
  ) {
    this.form = this.fb.group({});
    this.bankRows = this.fb.array([]);
    this.form.addControl(BANK_DETAILS_KEY, this.bankRows);
  }

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.pruneInactiveDocumentUploads());

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.initializeForm();
    });
    // When JSW re-auth succeeds the session blobs are refilled; rebuild the
    // form so the user gets the fresh prefill instead of a blank state.
    this.authFailedSub = this.jswAuthFailed$.subscribe((failed) => {
      if (this.lastAuthFailed && !failed) {
        this.initializeForm();
      }
      this.lastAuthFailed = failed;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.authFailedSub?.unsubscribe();
  }

  get isResubmitMode(): boolean {
    return !!(this.route.snapshot.paramMap.get("stgNo") || "").trim();
  }

  fieldHasResubmitHighlight(fieldKey: string): boolean {
    return this.highlightedResubmitKeys.has(fieldKey);
  }

  bankSectionHasResubmitHighlight(): boolean {
    return this.highlightedResubmitKeys.has(BANK_DETAILS_KEY);
  }

  /**
   * Idempotent form (re-)initialisation. Wipes previously-built controls
   * (so a re-auth-driven rebuild doesn't duplicate bank rows / controls)
   * before reading sessionStorage and building the dynamic form. Skips
   * entirely while JSW auth is failed -- the banner owns the screen until
   * the user re-authenticates.
   */
  private initializeForm(): void {
    this.loadError = "";
    this.errorMsg = "";
    this.highlightedResubmitKeys = new Set();
    this.resubmitPrefillPending = false;
    this.showValidationErrors = false;
    this.showDocumentErrors = false;
    this.documentUploads = {};

    this.fieldToSection.clear();
    this.originalSnapshot = {};
    this.originalBankRows = [];
    this.ifscLookupLoading = [];
    this.ifscLookupErrors = [];
    this.ifscFetchedValue = [];
    this.bankRowKeys = [...DEFAULT_BANK_KEYS];
    this.sections = [];
    this.requestorOptions = [];
    this.requestorSelectItems = [];
    this.selectedRequestorItem = null;
    this.selectedRequestorIdx = 0;

    Object.keys(this.form.controls).forEach((k) => this.form.removeControl(k));
    this.bankRows = this.fb.array([]);
    this.form.addControl(BANK_DETAILS_KEY, this.bankRows);

    if (this.jsw.isJswAuthFailed) {
      return;
    }

    try {
      this.requestorOptions = this.readSessionArray(
        JswVmdService.REQUESTOR_DETAILS_KEY,
      );
      this.requestorSelectItems = this.requestorOptions.map((opt, idx) => ({
        label: this.requestorOptionLabel(opt),
        idx,
      }));
      this.selectedRequestorIdx = 0;
      this.selectedRequestorItem = this.requestorSelectItems[0] ?? null;
      if (this.requestorOptions.length > 0) {
        this.requestorRecord =
          this.requestorOptions[this.selectedRequestorIdx] ??
          this.requestorOptions[0];
      } else {
        this.requestorRecord = this.readSessionRecord(
          JswVmdService.REQUESTOR_DETAILS_KEY,
        );
      }
      this.vendorRecord = this.readSessionRecord(
        JswVmdService.VENDOR_DETAILS_KEY,
      );

      if (
        Object.keys(this.requestorRecord).length === 0 &&
        Object.keys(this.vendorRecord).length === 0
      ) {
        this.loadError =
          "Vendor and requestor details are not available. Please refresh the page or sign in again.";
        return;
      }

      this.buildForm();

      const resubmitId = (this.route.snapshot.paramMap.get("stgNo") || "").trim();
      if (resubmitId) {
        this.loadResubmitPrefill(resubmitId);
      }
    } catch (err) {
      console.error("Failed to initialize change request form", err);
      this.loadError =
        "Unable to load vendor data from session. Please refresh and try again.";
    }
  }

  get hasFormChanges(): boolean {
    return this.computeEditedFields().length > 0;
  }

  get hasMissingRequiredDocuments(): boolean {
    return this.hasFormChanges && !this.validateRequiredDocuments();
  }

  /** True when any bank row has empty/invalid required bank fields. */
  get hasInvalidBankRows(): boolean {
    if (this.bankRows.length === 0) {
      return false;
    }
    for (let i = 0; i < this.bankRows.length; i++) {
      const fg = this.bankRowAt(i);
      for (const key of BANK_REQUIRED_FIELD_KEYS) {
        const c = fg.get(key);
        if (c && !c.disabled && c.invalid) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * True when any bank row is missing one of the mandatory IFSC-derived bank
   * fields. These are read-only, so they're checked by raw value (a successful
   * IFSC fetch populates them).
   */
  get hasIncompleteBankDetails(): boolean {
    for (let i = 0; i < this.bankRows.length; i++) {
      const raw = this.bankRowAt(i).getRawValue() as Record<string, unknown>;
      for (const key of BANK_REQUIRED_DISPLAY_FIELD_KEYS) {
        if (this.toInputValue(raw[key]).trim() === "") {
          return true;
        }
      }
    }
    return false;
  }

  get submitDisabled(): boolean {
    return (
      this.isSubmitting ||
      !this.hasFormChanges ||
      this.hasMissingRequiredDocuments ||
      this.hasInvalidBankRows ||
      this.hasIncompleteBankDetails ||
      !!this.loadError ||
      (this.isResubmitMode && this.resubmitPrefillPending)
    );
  }

  get submitButtonTitle(): string {
    if (!this.hasFormChanges) {
      return "Edit at least one field to enable submit";
    }
    if (this.hasInvalidBankRows) {
      return "Complete required bank fields (Account Holder, Account Number, IFSC code)";
    }
    if (this.hasIncompleteBankDetails) {
      return "Fetch bank details from the IFSC code to fill all required bank fields";
    }
    if (this.hasMissingRequiredDocuments) {
      return "Upload required supporting documents for your changes";
    }
    return "Submit change request";
  }

  cancel(): void {
    this.router.navigate(["/change-requests"]);
  }

  controlOf(name: string): AbstractControl | null {
    return this.form.get(name);
  }

  fieldHasValidationError(controlName: string): boolean {
    const c = this.controlOf(controlName);
    if (!c || c.disabled) {
      return false;
    }
    return (
      c.invalid && (c.touched || c.dirty || this.showValidationErrors)
    );
  }

  fieldValidationErrorMessage(controlName: string): string | null {
    const c = this.controlOf(controlName);
    if (!this.fieldHasValidationError(controlName) || !c) {
      return null;
    }
    return getValidationErrorMessage(c.errors);
  }

  bankFieldHasValidationError(rowIndex: number, bankKey: string): boolean {
    const c = this.bankRowAt(rowIndex).get(bankKey);
    if (!c || c.disabled) {
      return false;
    }
    if (
      bankKey === "IFSC_CODE" &&
      this.showValidationErrors &&
      this.ifscNeedsFetch(rowIndex)
    ) {
      return true;
    }
    const showRequiredHint =
      isBankFieldRequired(bankKey) &&
      this.hasInvalidBankRows &&
      this.hasFormChanges;
    return (
      c.invalid &&
      (c.touched || c.dirty || this.showValidationErrors || showRequiredHint)
    );
  }

  bankFieldValidationErrorMessage(
    rowIndex: number,
    bankKey: string,
  ): string | null {
    const c = this.bankRowAt(rowIndex).get(bankKey);
    if (!this.bankFieldHasValidationError(rowIndex, bankKey) || !c) {
      return null;
    }
    if (bankKey === "IFSC_CODE" && this.ifscNeedsFetch(rowIndex) && c.valid) {
      return "Click Fetch to load bank details for this IFSC code.";
    }
    return getValidationErrorMessage(c.errors);
  }

  fieldValidationTooltip(controlName: string): string | null {
    if (!this.fieldHasValidationError(controlName)) {
      return null;
    }
    const parsed = this.parseControlName(controlName);
    const key = parsed?.fieldKey ?? controlName;
    return (
      fieldValidationHint(key) ||
      this.fieldValidationErrorMessage(controlName)
    );
  }

  bankFieldValidationTooltip(rowIndex: number, bankKey: string): string | null {
    if (!this.bankFieldHasValidationError(rowIndex, bankKey)) {
      return null;
    }
    if (bankKey === "IFSC_CODE" && this.ifscNeedsFetch(rowIndex)) {
      const c = this.bankRowAt(rowIndex).get(bankKey);
      if (c?.valid) {
        return "Click Fetch to load bank details for this IFSC code.";
      }
    }
    return (
      fieldValidationHint(bankKey) ||
      this.bankFieldValidationErrorMessage(rowIndex, bankKey)
    );
  }

  fieldInputTitle(controlName: string, validationHint?: string): string {
    if (this.fieldHasValidationError(controlName)) {
      return this.fieldValidationTooltip(controlName) || "";
    }
    return validationHint || "";
  }

  bankFieldInputTitle(
    rowIndex: number,
    bankKey: string,
    validationHint?: string,
  ): string {
    if (this.bankFieldHasValidationError(rowIndex, bankKey)) {
      return this.bankFieldValidationTooltip(rowIndex, bankKey) || "";
    }
    return validationHint || "";
  }

  getGroupFields(
    section: FormSection,
    group: DocumentFieldGroup,
  ): SectionField[] {
    const keys = new Set(group.fieldKeys);
    const filtered = section.fields.filter((f) => keys.has(f.key));
    return sortFieldsByKeyOrder(filtered, group.fieldKeys);
  }

  getStandaloneVendorFields(section: FormSection): SectionField[] {
    const standalone = section.fields.filter(
      (f) => !isDocumentGroupFieldKey(f.key),
    );
    return sortFieldsByKeyOrder(standalone, VENDOR_STANDALONE_FIELD_ORDER);
  }

  documentsForGroup(groupId: string): File[] {
    return this.documentUploads[groupId] || [];
  }

  onDocumentsChange(groupId: string, files: File[]): void {
    if (files.length === 0) {
      const next = { ...this.documentUploads };
      delete next[groupId];
      this.documentUploads = next;
    } else {
      this.documentUploads = { ...this.documentUploads, [groupId]: files };
    }
    if (this.validateRequiredDocuments()) {
      this.showDocumentErrors = false;
    }
  }

  getRequiredDocumentGroupIds(): string[] {
    const ids = new Set<string>();
    for (const key of this.computeEditedFields()) {
      if (key === BANK_DETAILS_KEY) {
        continue;
      }
      const groupId = documentGroupIdForFieldKey(key);
      if (groupId) {
        ids.add(groupId);
      }
    }
    for (const i of this.getEditedBankRowIndices()) {
      ids.add(bankDocumentGroupId(i));
    }
    return [...ids];
  }

  getEditedBankRowIndices(): number[] {
    const docKeys = BANK_DOCUMENT_FIELD_KEYS.filter(
      (k) => this.bankRowKeys.includes(k) && isBankKeyEditable(k),
    );
    if (docKeys.length === 0) {
      return [];
    }
    const indices: number[] = [];
    for (let i = 0; i < this.bankRows.length; i++) {
      const current = this.snapshotBankRow(this.bankRowAt(i));
      const original = this.originalBankRows[i];
      if (!original) {
        indices.push(i);
        continue;
      }
      for (const k of docKeys) {
        if (this.toInputValue(current[k]) !== this.toInputValue(original[k])) {
          indices.push(i);
          break;
        }
      }
    }
    return indices;
  }

  isDocumentUploadEnabled(groupId: string): boolean {
    return this.getRequiredDocumentGroupIds().includes(groupId);
  }

  isDocumentUploadRequired(groupId: string): boolean {
    return this.getRequiredDocumentGroupIds().includes(groupId);
  }

  isDocumentUploadMissing(groupId: string): boolean {
    return (
      this.isDocumentUploadRequired(groupId) &&
      this.documentsForGroup(groupId).length === 0
    );
  }

  documentUploadShowError(groupId: string): boolean {
    return this.showDocumentErrors && this.isDocumentUploadMissing(groupId);
  }

  validateRequiredDocuments(): boolean {
    return !this.getRequiredDocumentGroupIds().some(
      (id) => this.documentsForGroup(id).length === 0,
    );
  }

  buildAttachmentsForSubmit(): ChangeRequestAttachmentRef[] {
    const refs: ChangeRequestAttachmentRef[] = [];
    for (const groupId of this.getRequiredDocumentGroupIds()) {
      if (!attachTypeForDocumentGroup(groupId)) {
        continue;
      }
      for (const file of this.documentsForGroup(groupId)) {
        refs.push({ groupId, file });
      }
    }
    return refs;
  }

  buildUploadedDocumentNames(): string[] {
    const names: string[] = [];
    for (const groupId of this.getRequiredDocumentGroupIds()) {
      for (const file of this.documentsForGroup(groupId)) {
        names.push(file.name);
      }
    }
    return names;
  }

  private pruneInactiveDocumentUploads(): void {
    const active = new Set(this.getRequiredDocumentGroupIds());
    let changed = false;
    const next = { ...this.documentUploads };
    for (const groupId of Object.keys(next)) {
      if (!active.has(groupId)) {
        delete next[groupId];
        changed = true;
      }
    }
    if (changed) {
      this.documentUploads = next;
      if (this.validateRequiredDocuments()) {
        this.showDocumentErrors = false;
      }
    }
  }

  isDateField(field: SectionField): boolean {
    return field.fieldType === "date";
  }

  private validateMsmeDateRange(): boolean {
    const edited = this.computeEditedFields();
    const touchesMsme =
      edited.includes("MSME_VALID_START_DATE") ||
      edited.includes("MSME_VALID_END_DATE");
    if (!touchesMsme) {
      return true;
    }
    const startCtrl = this.form.get(
      this.controlNameFor("MSME_VALID_START_DATE", "vendor"),
    );
    const endCtrl = this.form.get(
      this.controlNameFor("MSME_VALID_END_DATE", "vendor"),
    );
    const startStr = formatChangeRequestDateValue(startCtrl?.value);
    const endStr = formatChangeRequestDateValue(endCtrl?.value);
    const start = parseChangeRequestDate(startStr);
    const end = parseChangeRequestDate(endStr);
    if (!startStr || !endStr || !start || !end) {
      return true;
    }
    if (end.isBefore(start, "day")) {
      this.showValidationErrors = true;
      startCtrl?.markAsTouched();
      endCtrl?.markAsTouched();
      this.errorMsg =
        "MSME validity end date must be on or after the start date.";
      return false;
    }
    return true;
  }

  private scalarValueForField(
    fieldKey: string,
    sectionId: "requestor" | "vendor",
  ): string {
    const controlName = this.controlNameFor(fieldKey, sectionId);
    const raw = this.form.get(controlName)?.value;
    if (getFieldMeta(fieldKey)?.fieldType === "date") {
      return formatChangeRequestDateValue(raw);
    }
    return this.toInputValue(raw);
  }

  private toHtmlDateInputValue(value: unknown): string {
    const s = formatChangeRequestDateValue(value);
    if (!s) {
      return "";
    }
    return parseChangeRequestDate(s) ? s : "";
  }

  bankRowGroupId(rowIndex: number): string {
    return bankDocumentGroupId(rowIndex);
  }

  fieldUploadGroupId(formKey: string): string {
    return fieldDocumentGroupId(formKey);
  }

  bankRowAt(idx: number): FormGroup {
    return this.bankRows.at(idx) as FormGroup;
  }

  addBankRow(): void {
    this.bankRows.push(this.bankRowFormGroup({}));
    this.ifscLookupLoading.push(false);
    this.ifscLookupErrors.push("");
    this.ifscFetchedValue.push("");
  }

  canRemoveBankRow(idx: number): boolean {
    return idx >= this.originalBankRows.length;
  }

  removeBankRow(idx: number): void {
    if (!this.canRemoveBankRow(idx)) {
      return;
    }
    if (idx >= 0 && idx < this.bankRows.length) {
      this.bankRows.removeAt(idx);
      this.ifscLookupLoading.splice(idx, 1);
      this.ifscLookupErrors.splice(idx, 1);
      this.ifscFetchedValue.splice(idx, 1);
    }
  }

  canFetchIfsc(rowIndex: number): boolean {
    if (this.isSubmitting || this.ifscLookupLoading[rowIndex]) {
      return false;
    }
    const ctrl = this.bankRowAt(rowIndex).get("IFSC_CODE");
    if (!ctrl || ctrl.disabled) {
      return false;
    }
    return isValidIfsc(ctrl.value) && !ctrl.errors?.["ifsc"];
  }

  fetchIfscDetails(rowIndex: number): void {
    if (!this.canFetchIfsc(rowIndex)) {    
      return;
    }
    const ifscCtrl = this.bankRowAt(rowIndex).get("IFSC_CODE");
    const ifsc = String(ifscCtrl?.value ?? "").trim().toUpperCase();
    this.ifscLookupErrors[rowIndex] = "";
    this.ifscLookupLoading[rowIndex] = true;
    this.ifscLookup
      .lookup(ifsc)
      .pipe(
        finalize(() => {
          this.ifscLookupLoading[rowIndex] = false;
        }),
      )
      .subscribe({
        next: (mapped) => {
          this.ensureBankFieldsForRow(rowIndex);
          this.patchBankRowFromIfsc(rowIndex, mapped);
          this.ifscFetchedValue[rowIndex] = ifsc;
        },
        error: (err: Error) => {
          if (err instanceof IfscNotFoundError) {
            this.handleIfscNotFound(rowIndex);
            return;
          }
          this.ifscLookupErrors[rowIndex] =
            err?.message || "Unable to fetch bank details for this IFSC.";
        },
      });
  }

  private normalizeIfsc(v: unknown): string {
    return String(v ?? "").trim().toUpperCase();
  }

  /**
   * True when the row's current IFSC value is non-empty and differs from the
   * last successfully fetched value — i.e. the bank details are stale and a
   * fetch is required before submit.
   */
  ifscNeedsFetch(rowIndex: number): boolean {
    const current = this.normalizeIfsc(
      this.bankRowAt(rowIndex).get("IFSC_CODE")?.value,
    );
    if (current === "") {
      return false;
    }
    return current !== (this.ifscFetchedValue[rowIndex] ?? "");
  }

  /** Failsafe: auto-fetch bank details when focus leaves the IFSC field. */
  onIfscBlur(rowIndex: number): void {
    if (this.ifscNeedsFetch(rowIndex)) {
      this.fetchIfscDetails(rowIndex);
    }
  }

  /**
   * Handles a "no bank record for this IFSC" result: notifies the user via a
   * modal and, for a pre-existing bank row, reverts the IFSC control to its
   * original value. Newly-added rows keep the typed value so the user can edit.
   */
  private handleIfscNotFound(rowIndex: number): void {
    this.ifscLookupErrors[rowIndex] = "";
    if (rowIndex < this.originalBankRows.length) {
      const original = this.originalBankRows[rowIndex]?.["IFSC_CODE"] ?? "";
      this.bankRowAt(rowIndex).get("IFSC_CODE")?.setValue(original);
    }
    this.dialogs.showInfoDialog(
      "IFSC Not Found",
      "The IFSC code is not found in our database. Please contact the buyer to update it",
    );
  }

  submit(): void {
    if (
      this.isSubmitting ||
      this.loadError ||
      this.jsw.isJswAuthFailed ||
      (this.isResubmitMode && this.resubmitPrefillPending)
    ) {
      return;
    }
    this.errorMsg = "";

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.showValidationErrors = true;
      this.errorMsg = "Please fix validation errors before submitting.";
      return;
    }

    this.showValidationErrors = false;

    if (this.bankRows.controls.some((_, i) => this.ifscNeedsFetch(i))) {
      this.showValidationErrors = true;
      this.errorMsg =
        "Please click Fetch to load bank details for the changed IFSC code(s).";
      return;
    }

    if (this.hasIncompleteBankDetails) {
      this.errorMsg =
        "Please fetch bank details from the IFSC code to fill all required bank fields.";
      return;
    }

    const editedFields = this.computeEditedFields();
    if (editedFields.length === 0) {
      this.errorMsg = "No changes to submit. Edit at least one field first.";
      return;
    }

    if (!this.validateRequiredDocuments()) {
      this.showDocumentErrors = true;
      this.errorMsg =
        "Please upload required supporting documents for the fields you changed.";
      return;
    }
    this.showDocumentErrors = false;

    if (!this.validateMsmeDateRange()) {
      return;
    }

    const resubmitStgNo = this.isResubmitMode
      ? (this.route.snapshot.paramMap.get("stgNo") || "").trim()
      : "";
    if (this.isResubmitMode && !resubmitStgNo) {
      this.errorMsg =
        "Change request reference ID is missing. Cannot resubmit this request.";
      return;
    }

    const stgColumn691 = this.buildStgColumn691(editedFields);
    const updatedFields = this.buildUpdatedFields(editedFields);

    this.isSubmitting = true;
    this.jsw
      .submitChangeRequest({
        editedFields,
        stgColumn691,
        updatedFields,
        vendorRow: this.vendorRecord,
        requestorRow: this.requestorRecord,
        submitMode: this.isResubmitMode ? "update" : "insert",
        reuseStgNo: this.isResubmitMode ? resubmitStgNo : undefined,
        attachments: this.buildAttachmentsForSubmit(),
        uploadedDocumentNames: this.buildUploadedDocumentNames(),
      })
      .subscribe({
        next: (resp: SubmitChangeRequestResponse) => {
          this.isSubmitting = false;
          const stgNo = (resp && resp.stgNo) || "";
          const baseMsg = stgNo
            ? `Change request submitted successfully. Reference ID: ${stgNo}.`
            : "Change request submitted successfully.";
          const msg = resp && resp.mirrored === false
            ? `${baseMsg} (Audit record could not be saved; please notify support.)`
            : baseMsg;
          this.dialogs.showInfoDialog("Success", msg);
          this.router.navigate(["/change-requests"]);
        },
        error: (err) => {
          this.isSubmitting = false;
          console.error("Failed to submit change request", err);
          const message =
            (err && err.userMessage) ||
            (err && err.error && (err.error.message || err.error.error)) ||
            (err && err.message) ||
            "Unable to submit the change request. Please try again.";
          this.errorMsg =
            typeof message === "string"
              ? message
              : "Unable to submit the change request. Please try again.";
        },
      });
  }

  private loadResubmitPrefill(stgNo: string): void {
    const vendorCode = (localStorage.getItem("vendorCode") || "").trim();
    if (!vendorCode) {
      this.loadError =
        "Vendor code is missing. Please log in again before resubmitting.";
      return;
    }

    const fromState = this.readUpdatedFieldsFromHistoryState();
    if (fromState && Object.keys(fromState).length > 0) {
      this.applyResubmitPrefill(fromState);
      return;
    }

    this.resubmitPrefillPending = true;
    this.jsw
      .getChangeRequestDetailFromDb(vendorCode, stgNo)
      .pipe(takeUntil(this.destroy$), finalize(() => {
        this.resubmitPrefillPending = false;
      }))
      .subscribe({
      next: (doc) => {
        if (!doc) {
          this.loadError = "Change request not found.";
          return;
        }
        const st = String(doc.status || "").trim();
        if (st !== JswVmdService.CR_STATUS_REQUEST_REJECTED) {
          this.loadError =
            "Only requests with status REQUEST REJECTED can be edited and resubmitted.";
          return;
        }
        const uf = doc.updatedFields;
        if (uf && typeof uf === "object" && !Array.isArray(uf)) {
          this.applyResubmitPrefill(uf as Record<string, unknown>);
        }
      },
      error: (err) => {
        console.error("Failed to load change request detail", err);
        const message =
          (err && err.error && (err.error.message || err.error.error)) ||
          (err && err.message) ||
          "Unable to load this change request. Please try again.";
        this.loadError =
          typeof message === "string"
            ? message
            : "Unable to load this change request.";
      },
    });
  }

  private readUpdatedFieldsFromHistoryState(): Record<string, unknown> | null {
    if (typeof history === "undefined" || !history.state) {
      return null;
    }
    const st = history.state as Record<string, unknown>;
    const raw = st["updatedFields"];
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      return null;
    }
    return raw as Record<string, unknown>;
  }

  private applyResubmitPrefill(upd: Record<string, unknown>): void {
    this.highlightedResubmitKeys = new Set(Object.keys(upd));

    for (const key of Object.keys(upd)) {
      if (key === BANK_DETAILS_KEY) {
        const arr = upd[key];
        if (!Array.isArray(arr)) {
          continue;
        }
        while (this.bankRows.length > 0) {
          this.bankRows.removeAt(0);
        }
        this.originalBankRows = [];
        this.ifscLookupLoading = [];
        this.ifscLookupErrors = [];
        this.ifscFetchedValue = [];
        this.recomputeBankRowKeys(arr);
        for (const row of arr) {
          const r =
            row && typeof row === "object"
              ? (row as Record<string, unknown>)
              : {};
          const fg = this.bankRowFormGroup(r);
          this.bankRows.push(fg);
          this.originalBankRows.push(this.snapshotBankRow(fg));
          this.ifscLookupLoading.push(false);
          this.ifscLookupErrors.push("");
          this.ifscFetchedValue.push(this.normalizeIfsc(fg.get("IFSC_CODE")?.value));
        }
        continue;
      }

      const sectionId = this.fieldToSection.get(key);
      if (!sectionId) {
        continue;
      }
      const controlName = this.controlNameFor(key, sectionId);
      const ctrl = this.form.get(controlName);
      if (!ctrl) {
        continue;
      }
      const v = upd[key];
      let patch: string | number;
      if (v === null || v === undefined) {
        patch = "";
      } else if (typeof v === "number") {
        patch = v;
      } else if (typeof v === "boolean") {
        patch = v ? "true" : "false";
      } else {
        patch = String(v);
      }
      if (getFieldMeta(key)?.fieldType === "date") {
        ctrl.patchValue(this.toHtmlDateInputValue(patch));
      } else {
        ctrl.patchValue(patch);
      }
    }
  }

  private readSessionArray(key: string): Record<string, unknown>[] {
    const raw = sessionStorage.getItem(key);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return [];
      const obj = parsed as Record<string, unknown>;
      const arr = Array.isArray(obj["apiDataArray"]) ? obj["apiDataArray"] : [];
      return arr.filter(
        (r): r is Record<string, unknown> => !!r && typeof r === "object",
      );
    } catch {
      return [];
    }
  }

  private readSessionRecord(key: string): Record<string, unknown> {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return {};
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      console.warn(`Could not parse sessionStorage[${key}]`, e);
      return {};
    }
    if (!parsed || typeof parsed !== "object") {
      return {};
    }
    const obj = parsed as Record<string, unknown>;
    const arr = Array.isArray(obj["apiDataArray"]) ? obj["apiDataArray"] : [];
    if (arr.length > 0 && arr[0] && typeof arr[0] === "object") {
      return arr[0] as Record<string, unknown>;
    }
    if (Array.isArray(parsed) && parsed.length > 0) {
      return (parsed[0] as Record<string, unknown>) || {};
    }
    return obj;
  }

  private buildForm(): void {
    const requestorFields = this.collectScalarFields(
      this.requestorRecord,
      "requestor",
    );
    const vendorFields = this.collectScalarFields(this.vendorRecord, "vendor");

    vendorFields.forEach((f) => this.fieldToSection.set(f.key, "vendor"));
    requestorFields.forEach((f) => {
      if (!this.fieldToSection.has(f.key)) {
        this.fieldToSection.set(f.key, "requestor");
      }
    });

    this.sections = [];
    if (requestorFields.length > 0) {
      this.sections.push({
        id: "requestor",
        title: "Requestor Details",
        fields: requestorFields,
      });
    }
    if (vendorFields.length > 0) {
      this.sections.push({
        id: "vendor",
        title: "Vendor Details",
        fields: vendorFields,
      });
    }

    const bankSource = this.vendorRecord[BANK_DETAILS_KEY];
    this.recomputeBankRowKeys([]);
    if (Array.isArray(bankSource)) {
      bankSource.forEach((row) => {
        const r = (row && typeof row === "object" ? row : {}) as Record<
          string,
          unknown
        >;
        const fg = this.bankRowFormGroup(r);
        this.bankRows.push(fg);
        this.originalBankRows.push(this.snapshotBankRow(fg));
        this.ifscLookupLoading.push(false);
        this.ifscLookupErrors.push("");
        this.ifscFetchedValue.push(this.normalizeIfsc(fg.get("IFSC_CODE")?.value));
      });
    }
  }

  requestorOptionLabel(row: Record<string, unknown>): string {
    return Object.keys(row)
      .filter(
        (k) => this.isScalar(row[k]) && row[k] !== null && row[k] !== "",
      )
      .map((k) => String(row[k]))
      .join(" | ");
  }

  onRequestorChange(item: { label: string; idx: number } | null): void {
    if (!item) return;
    const row = this.requestorOptions[item.idx];
    if (!row) return;
    this.selectedRequestorIdx = item.idx;
    this.selectedRequestorItem = item;
    this.requestorRecord = row;
    const section = this.sections.find((s) => s.id === "requestor");
    if (!section) return;
    for (const field of section.fields) {
      const ctrl = this.form.get(field.controlName);
      if (ctrl) {
        const v = this.toInputValue(row[field.key]);
        ctrl.patchValue(v);
        this.originalSnapshot[field.controlName] = v;
      }
    }
  }

  /** Label for a bank column (template). */
  bankFieldLabel(key: string): string {
    return fieldPortalLabel(key, this.humanize(key));
  }

  bankFieldRequired(key: string): boolean {
    return isBankFieldRequired(key);
  }

  bankSupportingDocuments(key: string): string[] {
    return fieldSupportingDocuments(key);
  }

  bankValidationHint(key: string): string | undefined {
    return fieldValidationHint(key);
  }

  isBankFieldEditable(key: string): boolean {
    return isBankKeyEditable(key);
  }

  private mergeScalarKeysFromRows(rows: unknown[], into: Set<string>): void {
    if (!Array.isArray(rows)) {
      return;
    }
    for (const row of rows) {
      if (!row || typeof row !== "object" || Array.isArray(row)) {
        continue;
      }
      const rec = row as Record<string, unknown>;
      for (const k of Object.keys(rec)) {
        if (this.isScalar(rec[k])) {
          into.add(k);
        }
      }
    }
  }

  private orderBankKeys(keys: Set<string>): string[] {
    const ordered: string[] = [];
    for (const k of PREFERRED_BANK_KEY_ORDER) {
      if (keys.has(k)) {
        ordered.push(k);
        keys.delete(k);
      }
    }
    const rest = [...keys].sort((a, b) => a.localeCompare(b));
    return ordered.concat(rest);
  }

  /**
   * Rebuilds `bankRowKeys` from defaults, vendor session bank rows, and optional extra rows (e.g. resubmit prefill).
   */
  private recomputeBankRowKeys(extraRows: unknown[]): void {
    const s = new Set<string>([...DEFAULT_BANK_KEYS]);
    const vendorBank = this.vendorRecord[BANK_DETAILS_KEY];
    this.mergeScalarKeysFromRows(
      Array.isArray(vendorBank) ? vendorBank : [],
      s,
    );
    this.mergeScalarKeysFromRows(extraRows, s);
    this.bankRowKeys = this.orderBankKeys(s);
  }

  private bankRowFormGroup(values: Record<string, unknown>): FormGroup {
    const cfg: Record<string, unknown[]> = {};
    for (const k of this.bankRowKeys) {
      const initial = this.toInputValue(values[k]);
      const editable = isBankKeyEditable(k);
      cfg[k] = [
        { value: initial, disabled: !editable },
        buildValidators(getFieldMeta(k)),
      ];
    }
    return this.fb.group(cfg);
  }

  private snapshotBankRow(fg: FormGroup): Record<string, string> {
    const v = fg.getRawValue() as Record<string, unknown>;
    const snap: Record<string, string> = {};
    for (const k of this.bankRowKeys) {
      snap[k] = this.toInputValue(v[k]);
    }
    return snap;
  }

  private collectScalarFields(
    record: Record<string, unknown>,
    sectionId: "requestor" | "vendor",
  ): SectionField[] {
    const fields: SectionField[] = [];
    Object.keys(record || {}).forEach((key) => {
      if (key === BANK_DETAILS_KEY) {
        return;
      }
      const value = record[key];
      if (this.isScalar(value)) {
        const controlName = this.controlNameFor(key, sectionId);
        if (!this.form.contains(controlName)) {
          const meta = getFieldMeta(key);
          const editable = isFieldEditable(key, sectionId);
          const isDate = meta?.fieldType === "date";
          const controlValue = isDate
            ? this.toHtmlDateInputValue(value)
            : this.toInputValue(value);
          this.form.addControl(
            controlName,
            this.fb.control(
              { value: controlValue, disabled: !editable },
              buildValidators(meta),
            ),
          );
          this.originalSnapshot[controlName] = isDate
            ? formatChangeRequestDateValue(controlValue)
            : this.toInputValue(controlValue);
          fields.push({
            key,
            label: this.scalarFieldLabel(key, sectionId),
            type: typeof value === "number" ? "number" : "text",
            controlName,
            editable,
            fieldType: meta?.fieldType ?? "text",
            supportingDocuments: meta?.supportingDocuments ?? [],
            validationHint: meta?.validationHint,
          });
        }
      }
    });
    return fields;
  }

  private isScalar(v: unknown): boolean {
    return (
      v === null ||
      typeof v === "string" ||
      typeof v === "number" ||
      typeof v === "boolean"
    );
  }

  private controlNameFor(
    key: string,
    sectionId: "requestor" | "vendor",
  ): string {
    return `${sectionId}__${key}`;
  }

  private parseControlName(controlName: string): {
    sectionId: "requestor" | "vendor";
    fieldKey: string;
  } | null {
    const sep = controlName.indexOf("__");
    if (sep <= 0) {
      return null;
    }
    const sectionId = controlName.slice(0, sep);
    if (sectionId !== "requestor" && sectionId !== "vendor") {
      return null;
    }
    return {
      sectionId,
      fieldKey: controlName.slice(sep + 2),
    };
  }

  /** Portal label; requestor name lines are distinct from vendor name lines. */
  private scalarFieldLabel(
    key: string,
    sectionId: "requestor" | "vendor",
  ): string {
    if (sectionId === "requestor") {
      const nameMatch =
        /^NAME([1-4])$/.exec(key) || /^VENDOR_NAME([1-4])$/.exec(key);
      if (nameMatch) {
        return `Requestor name (line ${nameMatch[1]})`;
      }
    }
    return fieldPortalLabel(key, this.humanize(key));
  }

  private toInputValue(v: unknown): string {
    if (v === null || v === undefined) {
      return "";
    }
    if (typeof v === "string") {
      return v;
    }
    return String(v);
  }

  private humanize(key: string): string {
    return key
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private computeEditedFields(): string[] {
    const edited: string[] = [];
    Object.keys(this.originalSnapshot).forEach((controlName) => {
      const parsed = this.parseControlName(controlName);
      if (!parsed) {
        return;
      }
      const { sectionId, fieldKey } = parsed;
      if (!isControlEditableForDiff(fieldKey, sectionId)) {
        return;
      }
      const ctrl = this.form.get(controlName);
      if (ctrl?.disabled) {
        return;
      }
      const current =
        getFieldMeta(fieldKey)?.fieldType === "date"
          ? formatChangeRequestDateValue(ctrl?.value)
          : this.toInputValue(ctrl?.value);
      const original = this.toInputValue(this.originalSnapshot[controlName]);
      if (current !== original) {
        edited.push(fieldKey);
      }
    });
    if (this.areBankRowsEdited()) {
      edited.push(BANK_DETAILS_KEY);
    }
    return edited;
  }

  private areBankRowsEdited(): boolean {
    if (this.bankRowKeys.length === 0) {
      return false;
    }
    const current = this.bankRows.controls.map((c) =>
      this.snapshotBankRow(c as FormGroup),
    );
    if (current.length !== this.originalBankRows.length) {
      return true;
    }
    for (let i = 0; i < current.length; i++) {
      const a = current[i];
      const b = this.originalBankRows[i] || {};
      for (const k of this.bankRowKeys) {
        if (this.toInputValue(a[k]) !== this.toInputValue(b[k])) {
          return true;
        }
      }
    }
    return false;
  }

  /** Adds IFSC lookup target controls/keys to a bank row when missing. */
  private ensureBankFieldsForRow(rowIndex: number): void {
    const fg = this.bankRowAt(rowIndex);
    const keySet = new Set(this.bankRowKeys);
    let keysChanged = false;
    for (const k of IFSC_LOOKUP_FORM_KEYS) {
      if (!keySet.has(k)) {
        keySet.add(k);
        keysChanged = true;
      }
      if (!fg.contains(k)) {
        const editable = isBankKeyEditable(k);
        fg.addControl(
          k,
          this.fb.control(
            { value: "", disabled: !editable },
            buildValidators(getFieldMeta(k)),
          ),
        );
      }
    }
    if (keysChanged) {
      this.bankRowKeys = this.orderBankKeys(keySet);
    }
  }

  private patchBankRowFromIfsc(
    rowIndex: number,
    mapped: IfscBankFieldMap,
  ): void {
    const fg = this.bankRowAt(rowIndex);
    for (const k of IFSC_LOOKUP_FORM_KEYS) {
      if (!Object.prototype.hasOwnProperty.call(mapped, k)) {
        continue;
      }
      const ctrl = fg.get(k);
      if (ctrl) {
        ctrl.setValue(mapped[k], { emitEvent: true });
      }
    }
  }

  private buildStgColumn691(editedFields: string[]): Record<string, unknown> {
    const out: Record<string, unknown> = {};

    for (const key of STG_COLUMN691_TEMPLATE_KEYS) {
      if (key === BANK_DETAILS_KEY) {
        const sessionBank = this.vendorRecord[BANK_DETAILS_KEY];
        if (
          Array.isArray(sessionBank) &&
          sessionBank.length > 0 &&
          sessionBank[0] &&
          typeof sessionBank[0] === "object"
        ) {
          out[BANK_DETAILS_KEY] = [sessionBank[0]];
        } else {
          out[BANK_DETAILS_KEY] = [];
        }
        continue;
      }
      if (key === "PAN_NUMBER") {
        const p = this.vendorRecord["PAN_NUMBER"] ?? this.vendorRecord["PAN"];
        out["PAN_NUMBER"] = p !== undefined ? p : null;
        continue;
      }
      if (key === "MDRM_RECORD_NO") {
        const m =
          this.vendorRecord["MDRM_RECORD_NO"] ??
          this.vendorRecord["RECORD_NO"];
        out["MDRM_RECORD_NO"] = m !== undefined ? m : null;
        continue;
      }
      if (key === "SAP_VENDOR_CODE") {
        const sapCode =
          (this.vendorRecord["SAP_VENDOR_CODE"] as string | undefined) ||
          (this.vendorRecord["ERP_NO"] as string | undefined) ||
          (localStorage.getItem("vendorCode") || "").trim();
        out["SAP_VENDOR_CODE"] = sapCode ? sapCode : null;
        continue;
      }
      const v = this.vendorRecord[key];
      out[key] = v !== undefined ? v : null;
    }

    editedFields.forEach((fieldKey) => {
      if (fieldKey === BANK_DETAILS_KEY) {
        out[BANK_DETAILS_KEY] = this.collectBankRows();
        return;
      }
      const sectionId = this.fieldToSection.get(fieldKey);
      if (!sectionId) {
        return;
      }
      const controlName = this.controlNameFor(fieldKey, sectionId);
      out[fieldKey] = this.scalarValueForField(fieldKey, sectionId);
    });

    return out;
  }

  private buildUpdatedFields(
    editedFields: string[],
  ): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    editedFields.forEach((fieldKey) => {
      if (fieldKey === BANK_DETAILS_KEY) {
        out[BANK_DETAILS_KEY] = this.collectBankRows();
        return;
      }
      const sectionId = this.fieldToSection.get(fieldKey);
      if (!sectionId) {
        return;
      }
      out[fieldKey] = this.scalarValueForField(fieldKey, sectionId);
    });
    return out;
  }

  private collectBankRows(): Array<Record<string, unknown>> {
    return this.bankRows.controls.map((c) => {
      const v = (c as FormGroup).getRawValue() as Record<string, unknown>;
      const row: Record<string, unknown> = {};
      for (const k of this.bankRowKeys) {
        row[k] = this.toInputValue(v[k]);
      }
      return row;
    });
  }
}
